import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, CardBody, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Badge, Text, Input, HStack, Select, IconButton, useToast, Button, Box,
  Switch, Tooltip,
} from '@chakra-ui/react';
import { FiEdit2, FiCheck, FiX, FiTrash2, FiPlus, FiSave } from 'react-icons/fi';
import PageShell from '../components/PageShell';
import chargeableItemService from '../services/chargePresetService';

const ChargeableItems = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  // 'all' | 'active' | 'inactive'. Default is 'all' so the owner sees
  // both states — the previous list silently hid items they had
  // deactivated, which made it look like delete had succeeded.
  const [statusFilter, setStatusFilter] = useState('all');

  // Bulk edit mode
  const [bulkEdit, setBulkEdit] = useState(false);
  const [edits, setEdits] = useState({}); // { [id]: { name, price } }
  const [saving, setSaving] = useState(false);

  // Add new item state
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPrice, setNewPrice] = useState('');
  const [newCategoryId, setNewCategoryId] = useState('');
  // Inline "new category" mode — picked from the category dropdown's
  // "+ New category…" entry. Fresh hotels start with zero categories, so
  // this (plus the seed-defaults button) is their bootstrap path.
  const NEW_CATEGORY = '__new__';
  const [newCatName, setNewCatName] = useState('');
  const [newCatChargeType, setNewCatChargeType] = useState('other');
  const [newBillingMode, setNewBillingMode] = useState('one_time');
  // '' = use the category's charge type.
  const [newChargeType, setNewChargeType] = useState('');
  const [seedingDefaults, setSeedingDefaults] = useState(false);

  // Same list (values, labels, order) as CHARGEABLE_CATEGORIES in the IRF
  // signup form (frontdesko.app/onboard.html) — one taxonomy everywhere.
  const CHARGE_TYPES = [
    { value: 'food_beverage', label: t('chargePresets.chargeTypes.food_beverage') },
    { value: 'minibar', label: t('chargePresets.chargeTypes.minibar') },
    { value: 'laundry', label: t('chargePresets.chargeTypes.laundry') },
    { value: 'spa', label: t('chargePresets.chargeTypes.spa') },
    { value: 'fee', label: t('chargePresets.chargeTypes.fee'), shortLabel: t('chargePresets.chargeTypes.feeShort') },
    { value: 'other', label: t('chargePresets.chargeTypes.other') },
  ];

  const BILLING_MODES = [
    { value: 'one_time', label: t('chargePresets.billingModes.one_time') },
    { value: 'per_night', label: t('chargePresets.billingModes.per_night') },
    { value: 'per_night_per_adult', label: t('chargePresets.billingModes.per_night_per_adult') },
  ];
  const billingLabel = (mode) => (BILLING_MODES.find(m => m.value === mode) || BILLING_MODES[0]).label;
  const chargeTypeLabel = (value) => {
    const known = CHARGE_TYPES.find(ct => ct.value === value);
    if (known) return known.shortLabel || known.label;
    return (value || '').replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [itemsData, catsData] = await Promise.all([
        chargeableItemService.getAll(),
        chargeableItemService.getCategories(),
      ]);
      const itemsList = Array.isArray(itemsData) ? itemsData : itemsData?.results || [];
      const catsList = Array.isArray(catsData) ? catsData : catsData?.results || [];
      setItems(itemsList);
      setCategories(catsList);
    } catch { setError(t('chargePresets.errors.loadFailed')); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const getCatName = (item) => {
    if (item.category && typeof item.category === 'object') return item.category.name;
    if (item.category && typeof item.category === 'string') return item.category;
    const cat = categories.find(c => c.id === (item.category_id || item.category));
    return cat?.name || t('chargePresets.labels.uncategorized');
  };

  const getCatId = (item) => {
    if (item.category && typeof item.category === 'object') return item.category.id;
    return item.category_id || item.category;
  };

  const isItemActive = (item) => item.is_available !== false;

  const filtered = items.filter(item => {
    if (catFilter && String(getCatId(item)) !== catFilter) return false;
    if (statusFilter === 'active' && !isItemActive(item)) return false;
    if (statusFilter === 'inactive' && isItemActive(item)) return false;
    if (search) return item.name?.toLowerCase().includes(search.toLowerCase());
    return true;
  });

  const grouped = {};
  filtered.forEach(item => {
    const catName = getCatName(item);
    if (!grouped[catName]) grouped[catName] = [];
    grouped[catName].push(item);
  });

  // Bulk edit helpers
  const startBulkEdit = () => {
    const initial = {};
    items.forEach(item => {
      initial[item.id] = { name: item.name, price: String(parseFloat(item.price) || ''), billing_mode: item.billing_mode || 'one_time' };
    });
    setEdits(initial);
    setBulkEdit(true);
  };

  const cancelBulkEdit = () => {
    setBulkEdit(false);
    setEdits({});
  };

  const updateEdit = (id, field, value) => {
    setEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const saveBulkEdit = async () => {
    // Find changed items
    const changes = [];
    items.forEach(item => {
      const e = edits[item.id];
      if (!e) return;
      const origPrice = String(parseFloat(item.price) || '');
      const origMode = item.billing_mode || 'one_time';
      if (e.name !== item.name || e.price !== origPrice || e.billing_mode !== origMode) {
        changes.push({ id: item.id, name: e.name.trim(), price: parseFloat(e.price), billing_mode: e.billing_mode });
      }
    });

    if (changes.length === 0) {
      toast({ title: t('chargePresets.toasts.noChanges'), status: 'info', duration: 2000 });
      cancelBulkEdit();
      return;
    }

    setSaving(true);
    let ok = 0, fail = 0;
    for (const c of changes) {
      try {
        await chargeableItemService.update(c.id, { name: c.name, price: c.price });
        ok++;
      } catch { fail++; }
    }
    setSaving(false);
    toast({
      title: fail
        ? t('chargePresets.toasts.bulkUpdatedWithFailures', { count: ok, failed: fail })
        : t('chargePresets.toasts.bulkUpdated', { count: ok }),
      status: fail ? 'warning' : 'success',
      duration: 3000,
    });
    cancelBulkEdit();
    load();
  };

  // PATCH /toggle_availability/ flips is_available server-side and
  // returns the updated row. We splice it back into `items` so the
  // table reflects the new state without a full reload.
  const handleToggleAvailability = async (item) => {
    try {
      const updated = await chargeableItemService.toggleAvailability(item.id);
      setItems(prev => prev.map(it => (
        it.id === item.id
          ? { ...it, is_available: updated?.is_available ?? !isItemActive(it) }
          : it
      )));
      toast({
        title: (updated?.is_available ?? !isItemActive(item))
          ? t('chargePresets.toasts.activated', { name: item.name })
          : t('chargePresets.toasts.deactivated', { name: item.name }),
        status: 'success',
        duration: 2000,
      });
    } catch {
      toast({ title: t('chargePresets.toasts.updateFailed'), status: 'error', duration: 3000 });
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(t('chargePresets.confirm.delete', { name }))) return;
    try {
      await chargeableItemService.remove(id);
      toast({ title: t('chargePresets.toasts.deleted'), status: 'success', duration: 2000 });
      load();
    } catch (err) {
      // Backend returns 409 when the item is referenced by a POS
      // order line or a reservation addon — hard-delete would orphan
      // the audit trail. Offer the owner the soft-disable path.
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 409 && data?.in_use) {
        const orders = data.used_in_orders
          ? t('chargePresets.confirm.usageOrders', { count: data.used_in_orders }) : '';
        const addons = data.used_in_addons
          ? t('chargePresets.confirm.usageAddons', { count: data.used_in_addons }) : '';
        let usageLabel = t('chargePresets.confirm.usageExisting');
        if (orders && addons) usageLabel = t('chargePresets.confirm.usageBoth', { orders, addons });
        else if (orders || addons) usageLabel = orders || addons;
        if (window.confirm(t('chargePresets.confirm.inUse', { name, usage: usageLabel }))) {
          const item = items.find(it => it.id === id);
          if (item && isItemActive(item)) {
            await handleToggleAvailability(item);
          }
        }
        return;
      }
      toast({ title: t('chargePresets.toasts.deleteFailed'), status: 'error', duration: 3000 });
    }
  };

  const handleSeedDefaults = async () => {
    setSeedingDefaults(true);
    try {
      const result = await chargeableItemService.createDefaultCategories();
      toast({
        title: result?.created
          ? t('chargePresets.toasts.defaultsAdded', { count: result.created })
          : t('chargePresets.toasts.defaultsPresent'),
        status: 'success', duration: 2500,
      });
      load();
    } catch {
      toast({ title: t('chargePresets.toasts.defaultsFailed'), status: 'error', duration: 3000 });
    } finally { setSeedingDefaults(false); }
  };

  const handleAdd = async () => {
    const creatingCategory = newCategoryId === NEW_CATEGORY;
    if (!newName.trim() || !newPrice || !newCategoryId || (creatingCategory && !newCatName.trim())) {
      toast({ title: t('chargePresets.toasts.fillAllFields'), status: 'warning', duration: 2000 });
      return;
    }
    try {
      let categoryId = newCategoryId;
      if (creatingCategory) {
        // Backend bootstraps the default menu chain, so this works even on
        // a hotel with no POS setup at all.
        const created = await chargeableItemService.createCategory({
          name: newCatName.trim(),
          charge_type: newCatChargeType,
        });
        categoryId = created.id;
      }
      await chargeableItemService.create({
        name: newName.trim(),
        price: parseFloat(newPrice),
        category: parseInt(categoryId),
        is_available: true,
        billing_mode: newBillingMode,
        charge_type: newChargeType,
      });
      toast({ title: creatingCategory ? t('chargePresets.toasts.addedToNewCategory', { category: newCatName.trim() }) : t('chargePresets.toasts.added'), status: 'success', duration: 2000 });
      setNewName(''); setNewPrice(''); setNewCategoryId(''); setNewCatName('');
      setNewBillingMode('one_time'); setNewChargeType(''); setShowAdd(false);
      load();
    } catch (err) {
      toast({
        title: t('chargePresets.toasts.addFailed'),
        description: err?.response?.data?.detail || err?.response?.data?.name?.[0]
          || (typeof err?.response?.data === 'object' ? JSON.stringify(err.response.data) : ''),
        status: 'error', duration: 4000,
      });
    }
  };

  return (
    <PageShell
      title={t('chargePresets.title')}
      subtitle={t('chargePresets.subtitle')}
      breadcrumbs={[{ label: t('chargePresets.title') }]}
      loading={loading} error={error}
      actions={
        <HStack spacing={2}>
          {bulkEdit ? (
            <>
              <Button size="sm" colorScheme="green" leftIcon={<FiSave />} onClick={saveBulkEdit} isLoading={saving}>
                {t('chargePresets.buttons.saveAll')}
              </Button>
              <Button size="sm" variant="ghost" onClick={cancelBulkEdit}>{t('chargePresets.buttons.cancel')}</Button>
            </>
          ) : (
            <>
              <Button size="sm" variant="outline" leftIcon={<FiEdit2 />} onClick={startBulkEdit}>
                {t('chargePresets.buttons.editAll')}
              </Button>
              <IconButton icon={<FiPlus />} size="sm" colorScheme="blue" variant="outline"
                onClick={() => setShowAdd(!showAdd)} aria-label={t('chargePresets.buttons.addItem')} />
            </>
          )}
        </HStack>
      }
    >
      <HStack mb={3} spacing={3}>
        <Input size="sm" placeholder={t('chargePresets.filters.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} maxW="250px" />
        <Select size="sm" maxW="200px" value={catFilter} onChange={(e) => setCatFilter(e.target.value)}>
          <option value="">{t('chargePresets.filters.allCategories')}</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        <Select size="sm" maxW="160px" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">{t('chargePresets.filters.allStatus')}</option>
          <option value="active">{t('chargePresets.filters.activeOnly')}</option>
          <option value="inactive">{t('chargePresets.filters.inactiveOnly')}</option>
        </Select>
      </HStack>

      {/* Fresh hotel: no categories yet — offer one-click defaults so the
          first item can be added without leaving this screen. */}
      {!loading && categories.length === 0 && (
        <Card mb={3} shadow="sm" borderWidth="1px" borderColor="orange.200" bg="orange.50">
          <CardBody py={3} px={4}>
            <HStack justify="space-between" flexWrap="wrap" gap={2}>
              <Text fontSize="sm" color="gray.700">
                {t('chargePresets.emptyCategories.message')}
              </Text>
              <Button size="sm" colorScheme="orange" onClick={handleSeedDefaults} isLoading={seedingDefaults}>
                {t('chargePresets.emptyCategories.addDefaults')}
              </Button>
            </HStack>
          </CardBody>
        </Card>
      )}

      {showAdd && (
        <Card mb={3} shadow="sm" borderWidth="1px" borderColor="blue.200" bg="blue.50">
          <CardBody py={2} px={3}>
            <HStack spacing={2} align="start" flexWrap="wrap">
              <Input size="sm" placeholder={t('chargePresets.addForm.itemName')} value={newName} onChange={e => setNewName(e.target.value)} flex={2} minW="160px" bg="white" />
              <Input size="sm" placeholder={t('chargePresets.addForm.price')} type="number" value={newPrice} onChange={e => setNewPrice(e.target.value)} flex={1} minW="90px" bg="white" />
              <Select size="sm" placeholder={t('chargePresets.addForm.category')} value={newCategoryId} onChange={e => setNewCategoryId(e.target.value)} flex={1.5} minW="150px" bg="white">
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                <option value={NEW_CATEGORY}>{t('chargePresets.addForm.newCategoryOption')}</option>
              </Select>
              {newCategoryId === NEW_CATEGORY && (
                <>
                  <Input size="sm" placeholder={t('chargePresets.addForm.newCategoryName')} value={newCatName}
                    onChange={e => setNewCatName(e.target.value)} flex={1.5} minW="150px" bg="white"
                    list="default-category-suggestions" />
                  <datalist id="default-category-suggestions">
                    {CHARGE_TYPES.map(ct => ct.shortLabel || ct.label)
                      .map(name => <option key={name} value={name} />)}
                  </datalist>
                  <Select size="sm" value={newCatChargeType} onChange={e => setNewCatChargeType(e.target.value)}
                    flex={1.2} minW="140px" bg="white" title={t('chargePresets.addForm.categoryChargeTypeTitle')}>
                    {CHARGE_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
                  </Select>
                </>
              )}
              <Select size="sm" value={newBillingMode} onChange={e => setNewBillingMode(e.target.value)}
                flex={1.2} minW="150px" bg="white" title={t('chargePresets.addForm.billingTypeTitle')}>
                {BILLING_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </Select>
              <Select size="sm" value={newChargeType} onChange={e => setNewChargeType(e.target.value)}
                flex={1.4} minW="170px" bg="white" title={t('chargePresets.addForm.itemChargeTypeTitle')}>
                <option value="">
                  {(() => {
                    const cat = categories.find(c => String(c.id) === String(newCategoryId));
                    const inherited = newCategoryId === NEW_CATEGORY ? newCatChargeType : cat?.charge_type;
                    return inherited
                      ? t('chargePresets.addForm.asCategoryWithType', { type: chargeTypeLabel(inherited) })
                      : t('chargePresets.addForm.asCategory');
                  })()}
                </option>
                {CHARGE_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
              </Select>
              <IconButton icon={<FiCheck />} size="sm" colorScheme="green" variant="solid" onClick={handleAdd} aria-label={t('chargePresets.buttons.save')} />
              <IconButton icon={<FiX />} size="sm" variant="ghost" onClick={() => setShowAdd(false)} aria-label={t('chargePresets.buttons.cancel')} />
            </HStack>
          </CardBody>
        </Card>
      )}

      <Card shadow="sm" borderWidth="1px">
        <CardBody p={0}>
          <TableContainer>
            <Table size="sm">
              <Thead><Tr><Th>{t('chargePresets.columns.name')}</Th><Th>{t('chargePresets.columns.category')}</Th><Th>{t('chargePresets.columns.chargeType')}</Th><Th isNumeric>{t('chargePresets.columns.price')}</Th><Th>{t('chargePresets.columns.billing')}</Th><Th w="90px">{t('chargePresets.columns.active')}</Th><Th w="80px"></Th></Tr></Thead>
              <Tbody>
                {Object.entries(grouped).map(([catName, catItems]) => (
                  <React.Fragment key={catName}>
                    <Tr bg="gray.50">
                      <Td colSpan={7} py={1}>
                        <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">{catName}</Text>
                      </Td>
                    </Tr>
                    {catItems.map(item => {
                      const e = edits[item.id];
                      const active = isItemActive(item);
                      return (
                        <Tr key={item.id} _hover={{ bg: 'gray.50' }} opacity={active ? 1 : 0.55}>
                          <Td fontSize="sm">
                            {bulkEdit ? (
                              <Input size="xs" value={e?.name || ''} onChange={ev => updateEdit(item.id, 'name', ev.target.value)} />
                            ) : (
                              <HStack spacing={2}>
                                <Text fontWeight="500" textDecoration={active ? 'none' : 'line-through'}>{item.name}</Text>
                                {!active && (
                                  <Badge size="sm" fontSize="2xs" colorScheme="gray" variant="subtle">{t('chargePresets.labels.inactive')}</Badge>
                                )}
                              </HStack>
                            )}
                          </Td>
                          <Td><Badge fontSize="2xs">{getCatName(item)}</Badge></Td>
                          <Td>
                            <Text fontSize="xs" color="gray.500">
                              {(() => {
                                if (item.charge_type) return chargeTypeLabel(item.charge_type);
                                const catId = getCatId(item);
                                const cat = categories.find(c => c.id === catId);
                                return chargeTypeLabel(cat?.charge_type || '');
                              })()}
                            </Text>
                          </Td>
                          <Td isNumeric fontSize="sm">
                            {bulkEdit ? (
                              <Input size="xs" type="number" value={e?.price || ''} onChange={ev => updateEdit(item.id, 'price', ev.target.value)} w="90px" textAlign="right" />
                            ) : (
                              <Text fontWeight="500">{item.formatted_price || item.price}</Text>
                            )}
                          </Td>
                          <Td fontSize="sm">
                            {bulkEdit ? (
                              <Select size="xs" value={e?.billing_mode || 'one_time'} onChange={ev => updateEdit(item.id, 'billing_mode', ev.target.value)} w="110px">
                                {BILLING_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                              </Select>
                            ) : (
                              <Badge variant="outline" fontSize="2xs" colorScheme={item.billing_mode && item.billing_mode !== 'one_time' ? 'purple' : 'gray'}>
                                {billingLabel(item.billing_mode)}
                              </Badge>
                            )}
                          </Td>
                          <Td>
                            {!bulkEdit && (
                              <Tooltip
                                label={active ? t('chargePresets.labels.activeTooltip') : t('chargePresets.labels.inactiveTooltip')}
                                openDelay={400}
                              >
                                <Box display="inline-block">
                                  <Switch
                                    size="sm"
                                    colorScheme="green"
                                    isChecked={active}
                                    onChange={() => handleToggleAvailability(item)}
                                    aria-label={active ? t('chargePresets.labels.deactivateItem', { name: item.name }) : t('chargePresets.labels.activateItem', { name: item.name })}
                                  />
                                </Box>
                              </Tooltip>
                            )}
                          </Td>
                          <Td>
                            {!bulkEdit && (
                              <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red"
                                onClick={() => handleDelete(item.id, item.name)} aria-label={t('chargePresets.buttons.delete')} />
                            )}
                          </Td>
                        </Tr>
                      );
                    })}
                  </React.Fragment>
                ))}
                {filtered.length === 0 && <Tr><Td colSpan={7} textAlign="center" py={8} color="gray.400">{t('chargePresets.emptyState')}</Td></Tr>}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>
    </PageShell>
  );
};

export default ChargeableItems;
